import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Users, Search, Filter, ChevronRight, ChevronDown, Award, Calendar, TrendingUp, Building, Vote, Star, History, ExternalLink, RotateCcw, Layers, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { API } from "@/config";

// Province colors for visual distinction
const PROVINCE_COLORS = {
  "koshi": { primary: "#DC2626", secondary: "#FEE2E2", gradient: "from-red-600 to-red-800" },
  "madhesh": { primary: "#EA580C", secondary: "#FFEDD5", gradient: "from-orange-500 to-orange-700" },
  "bagmati": { primary: "#2563EB", secondary: "#DBEAFE", gradient: "from-blue-600 to-blue-800" },
  "gandaki": { primary: "#059669", secondary: "#D1FAE5", gradient: "from-emerald-600 to-emerald-800" },
  "lumbini": { primary: "#7C3AED", secondary: "#EDE9FE", gradient: "from-violet-600 to-violet-800" },
  "karnali": { primary: "#DB2777", secondary: "#FCE7F3", gradient: "from-pink-600 to-pink-800" },
  "sudurpashchim": { primary: "#0891B2", secondary: "#CFFAFE", gradient: "from-cyan-600 to-cyan-800" },
  "default": { primary: "#F77F00", secondary: "#FEF3C7", gradient: "from-amber-500 to-amber-700" }
};

const getProvinceColor = (provinceName) => {
  const key = provinceName?.toLowerCase()?.replace(/\s+/g, '')?.replace('province', '') || 'default';
  return PROVINCE_COLORS[key] || PROVINCE_COLORS.default;
};

const LiveTicker = () => (
  <div className="bg-gradient-to-r from-gorkhali-red via-peace-blue to-gorkhali-red py-3 overflow-hidden">
    <motion.div className="flex whitespace-nowrap" animate={{ x: [0, -2000] }} transition={{ duration: 25, repeat: Infinity, ease: "linear" }}>
      {[...Array(4)].map((_, i) => (
        <span key={i} className="mx-8 text-white font-bebas tracking-wider text-sm">
          🗳️ 165 CONSTITUENCIES • 77 DISTRICTS • 7 PROVINCES • EXPLORE NEPAL'S ELECTORAL MAP • <span className="text-marigold">★</span>
        </span>
      ))}
    </motion.div>
  </div>
);

const CandidateCard = ({ candidate, onClick }) => (
  <motion.div
    whileHover={{ y: -5, scale: 1.02 }}
    onClick={onClick}
    className="bg-white/5 backdrop-blur border border-white/10 rounded-lg p-4 cursor-pointer hover:border-marigold/50 transition-all"
  >
    <div className="flex items-start gap-4">
      {candidate.image_url ? (
        <img src={candidate.image_url} alt={candidate.name} className="w-16 h-16 rounded-full object-cover border-2" style={{ borderColor: candidate.party_color }} />
      ) : (
        <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
          <Users className="text-gray-400" size={24} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h3 className="font-bebas text-lg text-white tracking-wider truncate">{candidate.name}</h3>
        <p className="text-sm truncate" style={{ color: candidate.party_color }}>{candidate.party}</p>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs bg-white/10 px-2 py-1 rounded text-gray-300">{candidate.constituency}</span>
          {candidate.is_incumbent && (
            <span className="text-xs bg-marigold/20 text-marigold px-2 py-1 rounded flex items-center gap-1">
              <Award size={10} /> Incumbent
            </span>
          )}
        </div>
      </div>
    </div>
  </motion.div>
);

const ConstituencyCard = ({ constituency, onClick, index = 0 }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: index * 0.03, duration: 0.3 }}
    whileHover={{ y: -4, scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className="group bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur border border-white/10 rounded-xl p-4 cursor-pointer hover:border-marigold/50 hover:shadow-lg hover:shadow-marigold/10 transition-all duration-300"
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <motion.div 
          className="w-11 h-11 bg-gradient-to-br from-gorkhali-red/30 to-gorkhali-red/10 rounded-xl flex items-center justify-center group-hover:from-marigold/30 group-hover:to-marigold/10 transition-all duration-300"
          whileHover={{ rotate: [0, -10, 10, 0] }}
          transition={{ duration: 0.5 }}
        >
          <MapPin className="text-gorkhali-red group-hover:text-marigold transition-colors" size={20} />
        </motion.div>
        <div>
          <h3 className="font-bebas text-lg text-white tracking-wider group-hover:text-marigold transition-colors">{constituency.name}</h3>
          <p className="text-xs text-gray-400">{constituency.district}</p>
        </div>
      </div>
      <ChevronRight className="text-gray-500 group-hover:text-marigold group-hover:translate-x-1 transition-all" size={20} />
    </div>
  </motion.div>
);

// District Tile with expandable constituencies
const DistrictTile = ({ district, constituencies, provinceColor, onConstituencyClick, isExpanded, onToggle, index }) => {
  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        delay: index * 0.08,
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const contentVariants = {
    hidden: { height: 0, opacity: 0 },
    visible: { 
      height: "auto", 
      opacity: 1,
      transition: { 
        height: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
        opacity: { duration: 0.3, delay: 0.1 }
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="group"
    >
      {/* District Header Tile */}
      <motion.div
        onClick={onToggle}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        className={`relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-500 ${
          isExpanded 
            ? 'bg-gradient-to-r from-marigold/20 to-marigold/5 border-2 border-marigold/40 shadow-xl shadow-marigold/10' 
            : 'bg-gradient-to-br from-white/8 to-white/[0.02] border border-white/10 hover:border-white/20 hover:shadow-lg'
        }`}
      >
        {/* Animated background gradient */}
        <motion.div 
          className={`absolute inset-0 bg-gradient-to-r ${provinceColor.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}
        />
        
        {/* Shimmer effect */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
          initial={{ x: "-100%" }}
          animate={{ x: "100%" }}
          transition={{ duration: 3, repeat: Infinity, repeatDelay: 5 }}
        />

        <div className="relative p-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* District Icon */}
            <motion.div 
              className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                isExpanded 
                  ? 'bg-marigold/20' 
                  : 'bg-white/5 group-hover:bg-white/10'
              }`}
              style={{ 
                boxShadow: isExpanded ? `0 0 20px ${provinceColor.primary}30` : 'none'
              }}
              animate={isExpanded ? { rotate: [0, 5, -5, 0] } : {}}
              transition={{ duration: 0.5 }}
            >
              <Building 
                className={`transition-colors duration-300 ${isExpanded ? 'text-marigold' : 'text-gray-400 group-hover:text-white'}`} 
                size={26} 
              />
            </motion.div>

            <div>
              <h3 className={`font-bebas text-2xl tracking-wider transition-colors duration-300 ${
                isExpanded ? 'text-marigold' : 'text-white'
              }`}>
                {district}
              </h3>
              <div className="flex items-center gap-3 mt-1">
                <span className={`text-sm font-inter transition-colors ${
                  isExpanded ? 'text-marigold/70' : 'text-gray-400'
                }`}>
                  {constituencies.length} {constituencies.length === 1 ? 'Constituency' : 'Constituencies'}
                </span>
                <span 
                  className="w-2 h-2 rounded-full animate-pulse"
                  style={{ backgroundColor: provinceColor.primary }}
                />
              </div>
            </div>
          </div>

          {/* Expand/Collapse Button */}
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
              isExpanded 
                ? 'bg-marigold/20 text-marigold' 
                : 'bg-white/5 text-gray-400 group-hover:bg-white/10 group-hover:text-white'
            }`}
          >
            <ChevronDown size={22} />
          </motion.div>
        </div>
      </motion.div>

      {/* Expanded Constituencies */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="overflow-hidden"
          >
            <div className="pt-4 pl-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {constituencies.map((c, idx) => (
                <ConstituencyCard 
                  key={c.id} 
                  constituency={c} 
                  onClick={() => onConstituencyClick(c)}
                  index={idx}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Province Section Header
const ProvinceHeader = ({ province, districtCount, constituencyCount, color }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    className="mb-6 flex items-center gap-4"
  >
    <div 
      className={`w-1.5 h-12 rounded-full bg-gradient-to-b ${color.gradient}`}
    />
    <div>
      <h2 className="font-bebas text-3xl text-white tracking-wider flex items-center gap-3">
        <Globe className="text-marigold" size={28} />
        {province}
      </h2>
      <p className="text-gray-400 text-sm font-inter">
        {districtCount} Districts • {constituencyCount} Constituencies
      </p>
    </div>
  </motion.div>
);

const FALLBACK_AVATAR = "https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png";

const PartyBadge = ({ party, color }) => (
  <span 
    className="text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap"
    style={{ 
      backgroundColor: `${color}20`, 
      color: color,
      border: `1px solid ${color}40`
    }}
  >
    {party}
  </span>
);

const ConstituencyDetailModal = ({ constituency, onClose, onCandidateClick }) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (constituency) {
      fetchCandidates();
    }
  }, [constituency]);

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/constituencies/${constituency.id}/candidates`);
      setCandidates(response.data.candidates || []);
    } catch (error) {
      console.error("Failed to fetch candidates:", error);
      toast.error("Failed to load candidates");
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  if (!constituency) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-night-count border border-white/20 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 bg-gradient-to-r from-gorkhali-red/20 to-peace-blue/20">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gorkhali-red/30 rounded-full flex items-center justify-center">
                <MapPin className="text-gorkhali-red" size={28} />
              </div>
              <div>
                <h2 className="font-bebas text-3xl text-white tracking-wider">{constituency.name}</h2>
                <p className="text-gray-400">{constituency.district}, {constituency.province}</p>
                <span className="text-xs bg-white/10 px-2 py-1 rounded mt-2 inline-block text-gray-300 capitalize">
                  {constituency.type} Election
                </span>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">×</button>
          </div>
        </div>

        {/* Candidates List */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <h3 className="font-bebas text-xl text-white tracking-wider mb-4 flex items-center gap-2">
            <Users className="text-marigold" size={20} />
            WHO'S RUNNING ({candidates.length} candidates)
          </h3>

          {loading ? (
            <div className="flex justify-center py-10">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity }}
                className="w-10 h-10 border-4 border-marigold border-t-transparent rounded-full"
              />
            </div>
          ) : candidates.length > 0 ? (
            <div className="space-y-3">
              {candidates.map((candidate, idx) => (
                <motion.div
                  key={candidate.id || idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => onCandidateClick(candidate)}
                  className="bg-white/5 border border-white/10 rounded-lg p-4 cursor-pointer hover:border-marigold/50 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={candidate.image_url || FALLBACK_AVATAR}
                      alt={candidate.name}
                      className="w-14 h-14 rounded-full object-cover border-2"
                      style={{ borderColor: candidate.party_color || '#F77F00' }}
                      onError={(e) => { e.target.src = FALLBACK_AVATAR; }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h4 className="font-bebas text-lg text-white tracking-wider">{candidate.name}</h4>
                        <PartyBadge party={candidate.party} color={candidate.party_color || '#6B7280'} />
                      </div>
                      {candidate.votes_2022 && (
                        <p className="text-xs text-gray-400 mt-1">
                          2022 Votes: {candidate.votes_2022.toLocaleString()}
                        </p>
                      )}
                      {candidate.is_incumbent && (
                        <span className="text-xs bg-marigold/20 text-marigold px-2 py-0.5 rounded mt-1 inline-flex items-center gap-1">
                          <Award size={10} /> Incumbent
                        </span>
                      )}
                    </div>
                    <ChevronRight className="text-gray-500" size={20} />
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <Users className="mx-auto text-gray-600 mb-4" size={48} />
              <p className="text-gray-400 font-inter">No candidates registered yet for this constituency.</p>
              <p className="text-xs text-gray-500 mt-2">Check back during election season for updates.</p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const CandidateDetailModal = ({ candidate, onClose }) => {
  if (!candidate) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-night-count border border-white/20 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative p-6 border-b border-white/10">
          <div className="flex items-start gap-6">
            {candidate.image_url ? (
              <img src={candidate.image_url} alt={candidate.name} className="w-24 h-24 rounded-full object-cover border-4" style={{ borderColor: candidate.party_color }} />
            ) : (
              <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center">
                <Users className="text-gray-400" size={40} />
              </div>
            )}
            <div className="flex-1">
              <h2 className="font-bebas text-3xl text-white tracking-wider">{candidate.name}</h2>
              <p className="text-lg" style={{ color: candidate.party_color }}>{candidate.party}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="text-xs bg-white/10 px-3 py-1 rounded-full text-gray-300 flex items-center gap-1">
                  <MapPin size={12} /> {candidate.constituency}
                </span>
                <span className="text-xs bg-white/10 px-3 py-1 rounded-full text-gray-300 flex items-center gap-1">
                  <Building size={12} /> {candidate.district}
                </span>
                {candidate.is_incumbent && (
                  <span className="text-xs bg-marigold/20 text-marigold px-3 py-1 rounded-full flex items-center gap-1">
                    <Award size={12} /> Incumbent
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl">×</button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Bio */}
          {candidate.bio && (
            <div>
              <h3 className="font-bebas text-lg text-white tracking-wider mb-2 flex items-center gap-2">
                <Users size={18} className="text-marigold" /> BIOGRAPHY
              </h3>
              <p className="text-gray-300 font-inter text-sm leading-relaxed">{candidate.bio}</p>
            </div>
          )}

          {/* Election History */}
          {candidate.election_history && candidate.election_history.length > 0 && (
            <div>
              <h3 className="font-bebas text-lg text-white tracking-wider mb-3 flex items-center gap-2">
                <History size={18} className="text-rsp-blue" /> ELECTION HISTORY
              </h3>
              <div className="space-y-3">
                {candidate.election_history.map((election, idx) => (
                  <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bebas text-white">{election.year} - {election.election_type}</p>
                        <p className="text-xs text-gray-400">{election.constituency}</p>
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full font-bebas ${
                        election.result === "Won" ? "bg-congress-green/20 text-congress-green" : "bg-gorkhali-red/20 text-gorkhali-red"
                      }`}>
                        {election.result}
                      </span>
                    </div>
                    {election.votes && (
                      <p className="text-xs text-gray-500 mt-2">Votes: {election.votes.toLocaleString()}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
              <Calendar className="mx-auto text-marigold mb-2" size={24} />
              <p className="font-bebas text-xl text-white">{candidate.age || "N/A"}</p>
              <p className="text-xs text-gray-500">Age</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
              <TrendingUp className="mx-auto text-rsp-blue mb-2" size={24} />
              <p className="font-bebas text-xl text-white">{candidate.elections_contested || 0}</p>
              <p className="text-xs text-gray-500">Elections</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
              <Award className="mx-auto text-congress-green mb-2" size={24} />
              <p className="font-bebas text-xl text-white">{candidate.wins || 0}</p>
              <p className="text-xs text-gray-500">Wins</p>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Footer = () => (
  <footer className="bg-black py-12">
    <div className="max-w-6xl mx-auto px-4 text-center">
      <p className="text-gray-500 text-sm">© 2026 Nepali Ballot • Entertainment Only 🇳🇵</p>
    </div>
  </footer>
);

const ConstituenciesPage = () => {
  const [provinces, setProvinces] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedProvince, setSelectedProvince] = useState("all");
  const [selectedDistrict, setSelectedDistrict] = useState("all");
  const [selectedConstituencyFilter, setSelectedConstituencyFilter] = useState("all");
  const [electionType, setElectionType] = useState("federal");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modals
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedConstituency, setSelectedConstituency] = useState(null);
  
  // Expanded districts for grouped view
  const [expandedDistricts, setExpandedDistricts] = useState(new Set());

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [provRes, constRes, candRes] = await Promise.all([
        axios.get(`${API}/provinces`),
        axios.get(`${API}/constituencies`),
        axios.get(`${API}/real-candidates`)
      ]);
      setProvinces(Array.isArray(provRes.data) ? provRes.data : []);
      setConstituencies(Array.isArray(constRes.data) ? constRes.data : []);
      setCandidates(Array.isArray(candRes.data?.candidates) ? candRes.data.candidates : []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleConstituencyClick = (constituency) => {
    setSelectedConstituency(constituency);
  };

  const handleCandidateFromConstituency = (candidate) => {
    setSelectedConstituency(null);
    setSelectedCandidate(candidate);
  };

  const toggleDistrict = (district) => {
    setExpandedDistricts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(district)) {
        newSet.delete(district);
      } else {
        newSet.add(district);
      }
      return newSet;
    });
  };

  const expandAllDistricts = () => {
    const allDistricts = [...new Set(filteredConstituencies.map(c => c.district))];
    setExpandedDistricts(new Set(allDistricts));
  };

  const collapseAllDistricts = () => {
    setExpandedDistricts(new Set());
  };

  // Get unique districts based on selected province
  const districts = [...new Set(
    constituencies
      .filter(c => selectedProvince === "all" || c.province_id === selectedProvince)
      .map(c => c.district)
  )].sort();

  // Get constituencies in selected district (for Constituency dropdown)
  const constituenciesInDistrict = constituencies.filter(c => {
    const matchesProvince = selectedProvince === "all" || c.province_id === selectedProvince;
    const matchesDistrict = selectedDistrict === "all" || c.district === selectedDistrict;
    const matchesType = c.type === electionType;
    return matchesProvince && matchesDistrict && matchesType;
  });

  const handleResetFilters = () => {
    setSelectedProvince("all");
    setSelectedDistrict("all");
    setSelectedConstituencyFilter("all");
    setSearchQuery("");
    setExpandedDistricts(new Set());
  };

  // Filter constituencies
  const filteredConstituencies = constituencies.filter(c => {
    const matchesProvince = selectedProvince === "all" || c.province_id === selectedProvince;
    const matchesDistrict = selectedDistrict === "all" || c.district === selectedDistrict;
    const matchesConstituency = selectedConstituencyFilter === "all" || c.id === selectedConstituencyFilter || c.name === selectedConstituencyFilter;
    const matchesType = c.type === electionType;
    const matchesSearch = !searchQuery || 
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.district.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesProvince && matchesDistrict && matchesConstituency && matchesType && matchesSearch;
  });

  // Filter candidates
  const filteredCandidates = candidates.filter(c => {
    const matchesProvince = selectedProvince === "all" || c.province_id === selectedProvince;
    const matchesDistrict = selectedDistrict === "all" || c.district === selectedDistrict;
    const matchesConstituency = selectedConstituencyFilter === "all" || 
      c.constituency_id === selectedConstituencyFilter || 
      c.constituency === selectedConstituencyFilter;
    const matchesSearch = !searchQuery || 
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.party.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesProvince && matchesDistrict && matchesConstituency && matchesSearch;
  });

  // Group filtered candidates by constituency for display (sorted order from backend)
  const candidatesByConstituency = filteredCandidates.reduce((acc, c) => {
    const key = c.constituency || c.constituency_id || "Unknown";
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  // Group constituencies by province and district for the new view
  const constituenciesByProvinceAndDistrict = useMemo(() => {
    const grouped = {};
    filteredConstituencies.forEach(c => {
      const province = c.province || "Unknown Province";
      const district = c.district || "Unknown District";
      
      if (!grouped[province]) {
        grouped[province] = {};
      }
      if (!grouped[province][district]) {
        grouped[province][district] = [];
      }
      grouped[province][district].push(c);
    });
    
    // Sort provinces and districts
    const sortedGrouped = {};
    Object.keys(grouped).sort().forEach(province => {
      sortedGrouped[province] = {};
      Object.keys(grouped[province]).sort().forEach(district => {
        sortedGrouped[province][district] = grouped[province][district].sort((a, b) => 
          a.name.localeCompare(b.name)
        );
      });
    });
    
    return sortedGrouped;
  }, [filteredConstituencies]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-night-count">
        <motion.div className="text-center">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }} className="w-20 h-20 border-4 border-gorkhali-red border-t-transparent rounded-full mx-auto mb-4" />
          <p className="font-bebas text-2xl text-white">LOADING CONSTITUENCIES</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-night-count">
      <LiveTicker />

      {/* Hero */}
      <div className="relative py-16 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-gorkhali-red/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-congress-green/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }}>
            <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 3, repeat: Infinity }}>
              <MapPin className="mx-auto mb-6 text-marigold" size={64} />
            </motion.div>
            <h1 className="font-bebas text-6xl sm:text-7xl text-white tracking-wider mb-4" style={{ textShadow: "0 0 60px rgba(247,127,0,0.5)" }}>
              WHO'S RUNNING FROM WHERE
            </h1>
            <p className="font-playfair text-xl text-gray-300 italic">"Find candidates by constituency across all 7 provinces"</p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-16">
        {/* View All Official Candidates */}
        <div className="mb-8 p-5 bg-gradient-to-r from-marigold/20 to-peace-blue/20 border border-marigold/40 rounded-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="font-bebas text-xl text-white tracking-wider mb-1">VIEW ALL AVAILABLE CANDIDATES</h3>
              <p className="text-gray-300 text-sm">For the complete official 2026 candidate list, visit the Election Commission of Nepal</p>
            </div>
            <a
              href="https://election.gov.np"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-marigold hover:bg-marigold/80 text-peace-blue px-6 py-3 rounded-full font-bebas tracking-wider transition-all whitespace-nowrap"
            >
              <ExternalLink size={18} /> Election Commission
            </a>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bebas text-xl text-white tracking-wider flex items-center gap-2">
              <Filter className="text-marigold" size={20} /> SELECT DISTRICT • SELECT CONSTITUENCY
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetFilters}
              className="border-white/20 text-gray-300 hover:bg-white/10 hover:text-white"
            >
              <RotateCcw size={14} className="mr-2" /> Reset
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            {/* Election Type */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Election Type</label>
              <Select value={electionType} onValueChange={(v) => { setElectionType(v); setSelectedConstituencyFilter("all"); }}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="federal">Federal (HoR)</SelectItem>
                  <SelectItem value="provincial">Provincial Assembly</SelectItem>
                  <SelectItem value="local">Local Level</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Province */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Province</label>
              <Select value={selectedProvince} onValueChange={(v) => { setSelectedProvince(v); setSelectedDistrict("all"); setSelectedConstituencyFilter("all"); }}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select Province" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Provinces</SelectItem>
                  {provinces.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* District */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">District</label>
              <Select value={selectedDistrict} onValueChange={(v) => { setSelectedDistrict(v); setSelectedConstituencyFilter("all"); }}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select District" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Districts</SelectItem>
                  {districts.map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Constituency */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Constituency</label>
              <Select value={selectedConstituencyFilter} onValueChange={setSelectedConstituencyFilter}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select Constituency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Constituencies</SelectItem>
                  {constituenciesInDistrict.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Search */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  placeholder="Search name or party..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap gap-6 pt-4 border-t border-white/10">
            <motion.div 
              className="flex items-center gap-2"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-8 h-8 bg-gorkhali-red/20 rounded-lg flex items-center justify-center">
                <MapPin className="text-gorkhali-red" size={16} />
              </div>
              <div>
                <p className="font-bebas text-xl text-white leading-none">{filteredConstituencies.length}</p>
                <p className="text-xs text-gray-500">Constituencies</p>
              </div>
            </motion.div>
            <motion.div 
              className="flex items-center gap-2"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <div className="w-8 h-8 bg-marigold/20 rounded-lg flex items-center justify-center">
                <Building className="text-marigold" size={16} />
              </div>
              <div>
                <p className="font-bebas text-xl text-white leading-none">
                  {[...new Set(filteredConstituencies.map(c => c.district))].length}
                </p>
                <p className="text-xs text-gray-500">Districts</p>
              </div>
            </motion.div>
            <motion.div 
              className="flex items-center gap-2"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div className="w-8 h-8 bg-congress-green/20 rounded-lg flex items-center justify-center">
                <Users className="text-congress-green" size={16} />
              </div>
              <div>
                <p className="font-bebas text-xl text-white leading-none">{filteredCandidates.length}</p>
                <p className="text-xs text-gray-500">Candidates</p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="constituencies" className="space-y-6">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="constituencies" className="font-bebas tracking-wider data-[state=active]:bg-gorkhali-red data-[state=active]:text-white">
              <MapPin className="mr-2" size={16} /> CONSTITUENCIES
            </TabsTrigger>
            <TabsTrigger value="candidates" className="font-bebas tracking-wider data-[state=active]:bg-gorkhali-red data-[state=active]:text-white">
              <Users className="mr-2" size={16} /> CANDIDATES
            </TabsTrigger>
          </TabsList>

          <TabsContent value="constituencies">
            {filteredConstituencies.length > 0 ? (
              <div className="space-y-10">
                {/* Expand/Collapse All Controls */}
                <div className="flex items-center justify-between bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <Layers className="text-marigold" size={20} />
                    <span className="font-bebas text-lg text-white tracking-wider">
                      {Object.values(constituenciesByProvinceAndDistrict).reduce((acc, districts) => acc + Object.keys(districts).length, 0)} DISTRICTS
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-400 text-sm">Click districts to expand</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={expandAllDistricts}
                      className="border-white/20 text-gray-300 hover:bg-white/10 hover:text-white text-xs"
                    >
                      Expand All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={collapseAllDistricts}
                      className="border-white/20 text-gray-300 hover:bg-white/10 hover:text-white text-xs"
                    >
                      Collapse All
                    </Button>
                  </div>
                </div>

                {/* Grouped by Province and District */}
                {Object.entries(constituenciesByProvinceAndDistrict).map(([province, districtData], provinceIdx) => {
                  const provinceColor = getProvinceColor(province);
                  const districtCount = Object.keys(districtData).length;
                  const constituencyCount = Object.values(districtData).reduce((sum, arr) => sum + arr.length, 0);
                  
                  return (
                    <motion.section 
                      key={province}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: provinceIdx * 0.1 }}
                      className="space-y-4"
                    >
                      <ProvinceHeader 
                        province={province}
                        districtCount={districtCount}
                        constituencyCount={constituencyCount}
                        color={provinceColor}
                      />
                      
                      <div className="space-y-3">
                        {Object.entries(districtData).map(([district, districtConstituencies], districtIdx) => (
                          <DistrictTile
                            key={district}
                            district={district}
                            constituencies={districtConstituencies}
                            provinceColor={provinceColor}
                            onConstituencyClick={handleConstituencyClick}
                            isExpanded={expandedDistricts.has(district)}
                            onToggle={() => toggleDistrict(district)}
                            index={districtIdx}
                          />
                        ))}
                      </div>
                    </motion.section>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <MapPin className="mx-auto text-gray-500 mb-4" size={64} />
                </motion.div>
                <p className="font-bebas text-2xl text-white mb-2">NO CONSTITUENCIES FOUND</p>
                <p className="text-gray-400">Try adjusting your filters or search query.</p>
                <Button
                  onClick={handleResetFilters}
                  className="mt-4 bg-marigold hover:bg-marigold/80 text-peace-blue font-bebas"
                >
                  <RotateCcw size={16} className="mr-2" /> Reset Filters
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="candidates">
            {filteredCandidates.length > 0 ? (
              <div className="space-y-8">
                {Object.entries(candidatesByConstituency).map(([constituencyName, constituencyCandidates], groupIdx) => (
                  <motion.section
                    key={constituencyName}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: groupIdx * 0.05 }}
                  >
                    <div className="flex items-center gap-3 mb-4 pb-2 border-b border-white/10">
                      <MapPin className="text-marigold" size={20} />
                      <h3 className="font-bebas text-lg text-white tracking-wider">{constituencyName}</h3>
                      <span className="text-xs bg-white/10 px-2 py-1 rounded text-gray-400">
                        {constituencyCandidates.length} candidate{constituencyCandidates.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {constituencyCandidates.map((candidate, index) => (
                        <motion.div
                          key={candidate.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: groupIdx * 0.05 + index * 0.02 }}
                        >
                          <CandidateCard candidate={candidate} onClick={() => setSelectedCandidate(candidate)} />
                        </motion.div>
                      ))}
                    </div>
                  </motion.section>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Users className="mx-auto text-gray-500 mb-4" size={48} />
                <p className="text-gray-400">No candidates found. Try adjusting your filters.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Info Box */}
        <div className="mt-12 space-y-4">
          <div className="bg-marigold/10 border border-marigold/30 rounded-lg p-6">
            <h3 className="font-bebas text-xl text-marigold tracking-wider mb-2">📊 ABOUT NEPAL'S ELECTORAL SYSTEM</h3>
            <ul className="text-gray-300 text-sm space-y-2 font-inter">
              <li>• <strong>Federal (HoR):</strong> 275 seats (165 FPTP + 110 PR)</li>
              <li>• <strong>Provincial:</strong> 550 seats across 7 provinces</li>
              <li>• <strong>Local:</strong> 753 local units (municipalities & rural municipalities)</li>
            </ul>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-lg p-4">
            <h3 className="font-bebas text-sm text-gray-400 tracking-wider mb-2">DATA SOURCES</h3>
            <p className="text-gray-500 text-xs font-inter mb-3">
              Candidate information from Election Commission of Nepal, Ekantipur, Kantipur, and verified news sources.
            </p>
            <Link to="/sources" className="text-marigold hover:underline text-xs font-inter flex items-center gap-1">
              <ExternalLink size={12} /> View all data sources
            </Link>
          </div>
        </div>
      </div>

      {/* Candidate Detail Modal */}
      <AnimatePresence>
        {selectedCandidate && (
          <CandidateDetailModal 
            candidate={selectedCandidate} 
            onClose={() => setSelectedCandidate(null)} 
          />
        )}
      </AnimatePresence>

      {/* Constituency Detail Modal */}
      <AnimatePresence>
        {selectedConstituency && (
          <ConstituencyDetailModal 
            constituency={selectedConstituency} 
            onClose={() => setSelectedConstituency(null)}
            onCandidateClick={handleCandidateFromConstituency}
          />
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default ConstituenciesPage;
